import Model, { attr } from '@ember-data/model';

import type { DSSetup, RecordData, Slack, Timeframe } from 'webapp-types';
import type {
	GoalCalculationStrategy,
	GoalDeliveryType,
	GoalFormatType,
	GoalStatus,
	GoalTargetSuccessCondition,
	ScheduleType,
} from 'webapp/enums/enums';
import {
	GoalOwnerScheduleType,
	GoalProgressType,
	GoalTargetValueType,
	GoalType,
	TimeUnit,
	UserAccess,
} from 'webapp/enums/enums';

import type { HistoryValues, SubGoal } from 'webapp/models-custom/goal';

import type { GoalRangeTimeUnit } from 'webapp/services/model-manager/goals';

export default class GoalRecord extends Model {
	@attr('number') spaceId!: number;
	@attr('string') title!: string;
	@attr('string') description!: string;
	@attr('number', { defaultValue: GoalType.GOAL }) type!: GoalType;
	@attr('number') progressType!: GoalProgressType;
	@attr('number') ownerId!: number | null;
	@attr('string', { defaultValue: null }) groupId!: string | null;
	@attr('array', {
		defaultValue() {
			return [];
		},
	})
	tags!: number[];
	@attr('number') order!: number;
	@attr('number') blockId!: number | null;
	@attr('boolean', { defaultValue: false }) favorite!: boolean;
	@attr('object', {
		defaultValue() {
			return {};
		},
	})
	source!: RecordData.Goal.Source; // used in mobile pages where we have no metrics loaded
	@attr('number') status!: GoalStatus;
	@attr('number') created!: number;
	@attr('boolean') isArchived!: boolean;
	@attr('string') goalKeyLong!: string; // key on which all the historyValues will be accessible
	@attr('array', {
		defaultValue() {
			return [];
		},
	})
	subGoals!: SubGoal[];

	// Setup props (normalized from "setup")
	@attr('number', { defaultValue: TimeUnit.MONTH }) primaryGranulation!: GoalRangeTimeUnit;
	@attr('number', { defaultValue: GoalTargetValueType.FIXED })
	targetValueType!: GoalTargetValueType;
	@attr('number') targetValue!: number | null;
	@attr('string') endDate!: string | null;
	@attr('object', {
		defaultValue() {
			return {};
		},
	})
	historyValues!: HistoryValues;
	@attr('boolean', { defaultValue: false }) historyValuesOverride!: boolean;
	@attr('boolean', { defaultValue: false }) statusOverride!: boolean;
	@attr('string', { defaultValue: null })
	calculationStrategyOverride!: GoalCalculationStrategy | null;
	@attr('object-or-null') timeframe!: Timeframe | null;
	@attr('number') targetSuccessCondition!: GoalTargetSuccessCondition | null;
	@attr('number') decimals!: number | null;

	// for manual or subgoal goals ("Number", "Currency", "Percentage")
	@attr('number') formatType!: GoalFormatType | null;
	@attr('string') formatUnit!: string | null;
	@attr('number') startValue!: number | null;

	// for automatic goals ("From metric")
	@attr('string') mainMetricSetupKey!: string | null;
	@attr('number') mainMetricSetupSpaceAccessId!: number | null;
	@attr('array', {
		defaultValue() {
			return [];
		},
	})
	mainMetricSetupDimensions!: string[];
	@attr('object') mainMetricSetupFilters!: DSSetup.MetricSetupFiltersData | null;

	// for target value "Other metric"
	@attr('string') otherMetricSetupKey!: string | null;
	@attr('number') otherMetricSetupSpaceAccessId!: number | null;
	@attr('array', {
		defaultValue() {
			return [];
		},
	})
	otherMetricSetupDimensions!: string[];
	@attr('object') otherMetricSetupFilters!: DSSetup.MetricSetupFiltersData | null;

	// Permissions
	@attr('number', { defaultValue: UserAccess.PUBLIC }) access!: UserAccess;
	@attr('array', {
		defaultValue() {
			return [];
		},
	})
	users!: number[];

	// Notifications
	@attr('boolean', { defaultValue: true }) isDeliveryOn!: boolean;
	@attr('array', {
		defaultValue() {
			return [];
		},
	})
	delivery!: GoalDeliveryType[];
	@attr('array', {
		defaultValue() {
			return [];
		},
	})
	scheduleTypes!: ScheduleType[];
	@attr('array', {
		defaultValue() {
			return [];
		},
	})
	slackChannels!: Slack.Channel[];
	@attr('array', {
		defaultValue() {
			return [];
		},
	})
	recipients!: string[];
	@attr('number', { defaultValue: GoalOwnerScheduleType.MONTHLY })
	ownerScheduleType!: GoalOwnerScheduleType | null;

	// only for internal webapp use
	@attr('boolean') newBadge!: boolean;

	get allowedWithoutOKRsFeature() {
		return this.type !== GoalType.OBJECTIVE && this.progressType !== GoalProgressType.SUBGOALS;
	}
}
